import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { patientService } from '@/services/patientService';
import { Patient } from '@/types/patient.types';
import { useToast } from '@/hooks/use-toast';
import { Search, UserPlus, AlertTriangle, User, Calendar } from 'lucide-react';
import { formatDate } from '@/utils/formatters';
import { TakeoverPatientDialog } from './TakeoverPatientDialog';

interface SearchPatientForTakeoverProps {
  onSuccess?: (patient: Patient) => void;
  initialCnp?: string;
}

export const SearchPatientForTakeover: React.FC<SearchPatientForTakeoverProps> = ({
  onSuccess,
  initialCnp,
}) => {
  const { toast } = useToast();
  const normalizedInitialCnp = (initialCnp || '').replace(/\D/g, '').slice(0, 13);
  const [searchCnp, setSearchCnp] = useState(normalizedInitialCnp);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [takeoverDialogOpen, setTakeoverDialogOpen] = useState(false);
  const lastInitialCnp = useRef<string | null>(null);

  // Handle CNP input change
  const handleCnpChange = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length <= 13) {
      setSearchCnp(numericValue);
      setError(null);
    }
  };

  const searchPatient = useCallback(async (cnp: string) => {
    if (cnp.length !== 13) {
      setError('CNP-ul trebuie să conțină exact 13 cifre');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSearchResults([]);

    try {
      const patient = await patientService.searchPatientByCnp(cnp);

      if (!patient) {
        setError('Nu s-a găsit niciun pacient cu acest CNP');
        setSearchResults([]);
        return;
      }

      setSearchResults([patient]);
    } catch (err: any) {
      const errorMessage = err.message || 'Nu s-au putut căuta pacienții';
      setError(errorMessage);
      toast({
        title: 'Eroare la căutare',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSearching(false);
    }
  }, [toast]);

  // Handle search
  const handleSearch = async () => {
    await searchPatient(searchCnp);
  };

  useEffect(() => {
    if (normalizedInitialCnp.length !== 13 || lastInitialCnp.current === normalizedInitialCnp) {
      return;
    }

    lastInitialCnp.current = normalizedInitialCnp;
    setSearchCnp(normalizedInitialCnp);
    void searchPatient(normalizedInitialCnp);
  }, [normalizedInitialCnp, searchPatient]);

  // Handle takeover click
  const handleTakeoverClick = (patient: Patient) => {
    if (patient.can_takeover === false) {
      return;
    }
    setSelectedPatient(patient);
    setTakeoverDialogOpen(true);
  };

  // Handle successful takeover
  const handleTakeoverSuccess = (updatedPatient: Patient) => {
    setTakeoverDialogOpen(false);
    setSelectedPatient(null);
    setSearchResults([]);
    setSearchCnp('');

    toast({
      title: 'Pacient preluat cu succes',
      description: `${updatedPatient.name} este acum în gestiunea spitalului dvs.`,
    });

    onSuccess?.(updatedPatient);
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5" />
            Căutare rapidă pentru preluare
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search input */}
          <div className="space-y-2">
            <Label htmlFor="search-cnp">CNP pacient</Label>
            <div className="flex gap-2">
              <Input
                id="search-cnp"
                type="text"
                placeholder="Introduceți CNP-ul (13 cifre)"
                value={searchCnp}
                onChange={(e) => handleCnpChange(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={13}
                disabled={isSearching}
                className="font-mono"
              />
              <Button
                onClick={handleSearch}
                disabled={isSearching || searchCnp.length < 3}
              >
                {isSearching ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    Caută...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Caută
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Căutați pacienți după CNP pentru preluare rapidă
            </p>
          </div>

          {/* Error message */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  Rezultate găsite ({searchResults.length})
                </h3>
              </div>
              <div className="space-y-2">
                {searchResults.map((patient) => (
                  <div
                    key={patient.id}
                    className="border rounded-lg p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{patient.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {patient.patient_code}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="font-mono">
                            CNP: {patient.cnp || 'Nespecificat'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(patient.birth_date)}
                          </span>
                        </div>
                        {patient.hospital_name && (
                          <p className="text-xs text-muted-foreground">
                            Spital: {patient.hospital_name}
                          </p>
                        )}
                        {patient.can_takeover === false && patient.takeover_reason && (
                          <p className="text-xs text-destructive">
                            {patient.takeover_reason}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleTakeoverClick(patient)}
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        disabled={patient.can_takeover === false}
                      >
                        <UserPlus className="mr-1 h-4 w-4" />
                        Preia
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info message when no search */}
          {!isSearching && searchResults.length === 0 && !error && searchCnp.length === 0 && (
            <Alert>
              <AlertDescription className="text-sm">
                Introduceți CNP-ul pacientului pentru a căuta și prelua un pacient.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Takeover Dialog */}
      <TakeoverPatientDialog
        isOpen={takeoverDialogOpen}
        patient={selectedPatient}
        onClose={() => {
          setTakeoverDialogOpen(false);
          setSelectedPatient(null);
        }}
        onSuccess={handleTakeoverSuccess}
      />
    </>
  );
};
